import AiconeLogo from '@/components/config_components/AiconeLogo/AiconeLogo.vue'
const NAV_HISTORY_KEY = "config_nav_history";

const ROUTE_LABELS = {
  GlobalOverview: "Overview",
  ProjectManagement: "Projects",
  UserManagement: "Users",
  RolesManagement: "Roles",
  EntityConfigs: "Entity Configs",
  EntityConfigDetail: "Entity Details",
  EntitySubsystemBindings: "Subsystem Bindings",
  CreateForm: "Create Form",
  CreateSections: "Create Sections",
  ManageQuestions: "Manage Questions",
  PreviewForm: "Preview Form",
  ViewTemplate: "View Templates",
  UploadTemplate: "Upload Template",
  EsigningSetup: "E-signing Setup",
  TemplateFormManagement: "Template Form Management",
  TemplateQuestionMapping: "Template Question Mapping",
  ConfigureDocusealTemplate: "Configure Docuseal Template",
  EsigningWorkflows: "eSigning Workflows",
  CreateWorkflow: "Create Workflow",
  ConfigureWorkflow: "Configure Workflow",
  ManageStatus: "Manage Status",
  CreateRiskFramework: "Create Risk Framework",
  ComplianceManagement: "Compliance Management",
  ManageDocuments: "Documents",
  ManageSubEntities: "Manage Sub Entities",
  SubEntityRequirements: "Sub Entity Requirements",
  VerificationServices: "Verification Services",
  ConfigureVerificationServices: "Configure Verification Services",
  ConfigureVerificationForm: "Configure Verification Form",
  VerificationForm: "Verification Forms",
  FormQuestionMapping: "Form Question Mapping",
  ManualVerification: "Manual Verification",
  ManualVerificationBinding: "Manual Verification Binding",
};

const SYSTEM_HOME_ROUTE = { name: "GlobalOverview" };
const ROUTE_LABEL_MAP = new Map(Object.entries(ROUTE_LABELS));
const ROUTE_QUERY_LABEL_RESOLVERS = [
  (route) => route.query?.form_name,
  (route) => route.query?.entity_type_name,
  (route) => route.query?.workflow_name,
  (route) => route.query?.template_name,
  (route) => route.query?.project_name,
];
const MAX_TRAIL_LENGTH = 6;

function prettifyRouteName(routeName) {
  return routeName
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim();
}

function parseTrailJson(rawTrail) {
  try {
    return JSON.parse(rawTrail || "[]");
  } catch (_error) {
    return [];
  }
}

function readStoredTrail() {
  const storedTrail = parseTrailJson(sessionStorage.getItem(NAV_HISTORY_KEY));
  return Array.isArray(storedTrail) ? storedTrail : [];
}

function saveStoredTrail(trail) {
  try {
    sessionStorage.setItem(NAV_HISTORY_KEY, JSON.stringify(trail));
  } catch (_error) {
    // Session storage can be unavailable in some privacy modes.
  }
}

export default {
  name: "ConfigNavbar",
  components: {AiconeLogo},
  props: {
    hide_hamburger: {
      type: Boolean,
      default: false,
    },
    show_mobile_drawer: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    navTrail: [],
  }),
  computed: {
    breadcrumbItems() {
      const trail = this.navTrail.slice(-MAX_TRAIL_LENGTH);
      const trailItems = trail.map((entry, index) => ({
        label: entry.label,
        to: entry.to,
        current: index === trail.length - 1,
      }));

      return [{ label: "System", to: SYSTEM_HOME_ROUTE }, ...trailItems];
    },
    currentRouteLabel() {
      return this.resolveRouteLabel(this.$route);
    },
  },
  mounted() {
    this.syncTrailWithRoute(this.$route, true);
  },
  watch: {
    $route(to) {
      this.syncTrailWithRoute(to, false);
    },
  },
  methods: {
    resolveEntityConfigLabel(route) {
      const projectName = String(route.query?.project_name || "").trim();
      return projectName ? `Entity Configurations for ${projectName}` : null;
    },
    resolveQueryLabel(route) {
      const label = ROUTE_QUERY_LABEL_RESOLVERS
        .map((resolveLabel) => resolveLabel(route))
        .find((value) => String(value || "").trim());

      return label ? String(label).trim() : null;
    },
    resolveNamedRouteLabel(route) {
      if (route.name && ROUTE_LABEL_MAP.has(route.name)) {
        return ROUTE_LABEL_MAP.get(route.name);
      }

      return typeof route.name === "string" ? prettifyRouteName(route.name) : null;
    },
    resolveRouteLabel(route) {
      if (!route) return "Unknown";

      return (route.name === "EntityConfigs" && this.resolveEntityConfigLabel(route))
        || this.resolveQueryLabel(route)
        || this.resolveNamedRouteLabel(route)
        || "Current Page";
    },
    routeToTrailEntry(route) {
      const to = route.name
        ? {
            name: route.name,
            params: { ...route.params },
            query: { ...route.query },
          }
        : {
            path: route.path,
            query: { ...route.query },
          };

      const routevar ={
        label: this.resolveRouteLabel(route),
        to,
        fullPath: route.fullPath,
      };

      return routevar;
    },
    getStoredTrail() {
      return readStoredTrail();
    },
    mergeTrailEntry(storedTrail, entry) {
      const existingIndex = storedTrail.findIndex((item) => item.fullPath === entry.fullPath);
      return existingIndex >= 0
        ? storedTrail.slice(0, existingIndex + 1)
        : [...storedTrail, entry];
    },
    persistTrail(trail) {
      saveStoredTrail(trail);
    },
    syncTrailWithRoute(route, replaceAll = false) {
      if (!route?.name && !route?.path) return;

      const entry = this.routeToTrailEntry(route);
      const storedTrail = replaceAll ? [] : this.getStoredTrail();
      const trimmedTrail = this.mergeTrailEntry(storedTrail, entry).slice(-MAX_TRAIL_LENGTH);
      this.navTrail = trimmedTrail;
      this.persistTrail(trimmedTrail);
    },
    isClickable(item) {
      return Boolean(item.to?.name || item.to?.path);
    },
  },
};
